import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Section } from "../types";
import { SiSpotify, SiYoutube, SiSoundcloud, SiItunes } from 'react-icons/si';
import { FaDeezer } from 'react-icons/fa';

type ReleasesSection = Extract<Section, { type: "releases" }>;
type Item = {
  title: string;
  cover: string;
  year?: string;
  description?: string;
  links?: Record<string, string>;
};
type ApiResponse = { items: Item[] };

// ======== STREAMS SUPORTADOS ========
// ordem preferida no CTA
const ALLOWED_PLATFORMS = ['spotify', 'youtube', 'soundcloud', 'deezer', 'itunes'] as const;
type Allowed = typeof ALLOWED_PLATFORMS[number];

// normaliza chaves vindas do resolver/Spotify/etc.
// OBS: não aceitamos "youtubemusic"/"ytmusic" — só "youtube" puro.
function normalizeKey(k: string): Allowed | null {
  const s = k.toLowerCase();
  if (s === 'spotify') return 'spotify';
  if (s === 'youtube') return 'youtube';
  if (s === 'soundcloud') return 'soundcloud';
  if (s === 'deezer') return 'deezer';
  if (s === 'itunes') return 'itunes';
  return null; // ignora o resto (appleMusic, youtubemusic, tidal, etc.)
}

// filtra/ordena apenas as plataformas suportadas
function platformEntries(links?: Record<string, string>) {
  if (!links) return [] as Array<[Allowed, string]>;
  const norm = Object.entries(links)
    .map(([k, v]) => [normalizeKey(k), v] as const)
    .filter((p): p is [Allowed, string] => p[0] !== null);

  norm.sort(
    (a, b) => ALLOWED_PLATFORMS.indexOf(a[0]) - ALLOWED_PLATFORMS.indexOf(b[0])
  );
  return norm;
}

// ícones SVG via react-icons
function iconFor(key: Allowed) {
  if (key === 'spotify') return <SiSpotify size={18} />;
  if (key === 'youtube') return <SiYoutube size={18} />;
  if (key === 'soundcloud') return <SiSoundcloud size={18} />;
  if (key === 'deezer') return <FaDeezer size={18} />;
  // key === 'itunes'
  return <SiItunes size={18} />;
}

export default function Releases({ section }: { section: ReleasesSection }) {
  const isSpotify = (section as any).source === "spotify";
  const manual = (section as any).items as Item[] | undefined;

  const [autoItems, setAutoItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // carrega automático (Spotify) quando configurado
  useEffect(() => {
    if (!isSpotify) return;
    const artistId = (section as any).artistId as string;
    if (!artistId) return;
    const limit = (section as any).limit ?? 9;
    const resolver = (section as any).linkResolver ?? "odesli";
    const url = `/api/spotify/releases?artistId=${encodeURIComponent(
      artistId
    )}&limit=${limit}&resolver=${resolver}`;
    let alive = true;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json() as Promise<ApiResponse>;
      })
      .then((json) => {
        if (alive) setAutoItems(json.items || []);
      })
      .catch(() => {
        if (alive) setError("Não foi possível carregar lançamentos automáticos.");
      });
    return () => {
      alive = false;
    };
  }, [isSpotify, section]);

  const items: Item[] = useMemo(
    () => (isSpotify ? autoItems || [] : manual || []),
    [isSpotify, autoItems, manual]
  );

  // ========================
  //   CAROUSEL 3D SANDBOX
  // ========================
  const [active, setActive] = useState(0);
  const areaRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // offsets responsivos baseados na largura real do card
  const [tx1, setTx1] = useState(240); // ~ sandbox: 240px
  const [tx2, setTx2] = useState(480); // ~ sandbox: 480px
  const [z1, setZ1] = useState(-400);
  const [z2, setZ2] = useState(-500);
  const rot = 35; // graus (±)
  const [cardW, setCardW] = useState(360);
  const ARROW_W = 56;
  const ARROW_GAP = 8;

  useEffect(() => {
    const recalc = () => {
      const w = cardRef.current?.getBoundingClientRect().width || 360;
      // proporções do sandbox (240px desloca ~66% do card de 362px)
      const f = w / 362; // escala relativa ao sandbox
      setTx1(240 * f);
      setTx2(480 * f);
      setZ1(-400 * f);
      setZ2(-500 * f);
      setCardW(w);
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, []);

  const bottomW = cardW + 2 * (ARROW_W + ARROW_GAP);

  const next = () =>
    setActive((i) => (items.length ? (i + 1) % items.length : 0));
  const prev = () =>
    setActive((i) =>
      items.length ? (i - 1 + items.length) % items.length : 0
    );

  // swipe (mobile/desktop)
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;

    let down = false, sx = 0, dx = 0;

    const setDragging = (v: boolean) => {
      if (v) el.classList.add('is-dragging');
      else el.classList.remove('is-dragging');
    };

    // helpers
    const start = (x: number) => { down = true; sx = x; dx = 0; setDragging(true); };
    const move = (x: number, ev: Event) => {
      if (!down) return;
      dx = x - sx;
      if (Math.abs(dx) > 8) (ev as any).preventDefault?.(); // não deixa o doc “andar” lateral
    };
    const end = () => {
      if (!down) return;
      down = false; setDragging(false);
      if (dx > 40) prev();
      else if (dx < -40) next();
    };

    // POINTER (quando disponível)
    const pd = (e: PointerEvent) => start(e.clientX);
    const pm = (e: PointerEvent) => move(e.clientX, e);
    const pu = () => end();

    el.addEventListener('pointerdown', pd, { passive: false });
    window.addEventListener('pointermove', pm, { passive: false });
    window.addEventListener('pointerup', pu);
    window.addEventListener('pointercancel', pu);
    window.addEventListener('blur', pu);

    // FALLBACK mouse
    const md = (e: MouseEvent) => start(e.clientX);
    const mm = (e: MouseEvent) => move(e.clientX, e);
    const mu = () => end();

    el.addEventListener('mousedown', md, { passive: false });
    window.addEventListener('mousemove', mm, { passive: false });
    window.addEventListener('mouseup', mu);

    // FALLBACK touch
    const td = (e: TouchEvent) => start(e.touches[0].clientX);
    const tm = (e: TouchEvent) => move(e.touches[0].clientX, e);
    const tu = () => end();

    el.addEventListener('touchstart', td, { passive: false });
    window.addEventListener('touchmove', tm, { passive: false });
    window.addEventListener('touchend', tu);

    // trackpad/roda – deslize horizontal
    const onWheel = (e: WheelEvent) => {
      const ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY);
      if (ax > ay && ax > 6) {
        e.preventDefault();
        if (e.deltaX > 0) next();
        else if (e.deltaX < 0) prev();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('pointerdown', pd);
      window.removeEventListener('pointermove', pm);
      window.removeEventListener('pointerup', pu);
      window.removeEventListener('pointercancel', pu);
      window.removeEventListener('blur', pu);

      el.removeEventListener('mousedown', md);
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);

      el.removeEventListener('touchstart', td);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', tu);

      el.removeEventListener('wheel', onWheel);
    };
  }, [items.length, prev, next]);

  // estilos idênticos ao sandbox (sem título/ano; só capas)
  const styleFor = (index: number): React.CSSProperties => {
    if (active === index)
      return {
        opacity: 1,
        transform: "translateX(0px) translateZ(0px) rotateY(0deg)",
        zIndex: 10,
      };
    else if (active - 1 === index)
      return {
        opacity: 1,
        transform: `translateX(${-tx1}px) translateZ(${z1}px) rotateY(${rot}deg)`,
        zIndex: 9,
      };
    else if (active + 1 === index)
      return {
        opacity: 1,
        transform: `translateX(${tx1}px) translateZ(${z1}px) rotateY(${-rot}deg)`,
        zIndex: 9,
      };
    else if (active - 2 === index)
      return {
        opacity: 1,
        transform: `translateX(${-tx2}px) translateZ(${z2}px) rotateY(${rot}deg)`,
        zIndex: 8,
      };
    else if (active + 2 === index)
      return {
        opacity: 1,
        transform: `translateX(${tx2}px) translateZ(${z2}px) rotateY(${-rot}deg)`,
        zIndex: 8,
      };
    else if (index < active - 2)
      return {
        opacity: 0,
        transform: `translateX(${-tx2}px) translateZ(${z2}px) rotateY(${rot}deg)`,
        zIndex: 7,
        pointerEvents: "none",
      };
    else
      return {
        opacity: 0,
        transform: `translateX(${tx2}px) translateZ(${z2}px) rotateY(${-rot}deg)`,
        zIndex: 7,
        pointerEvents: "none",
      };
  };

  if (!items.length) {
    return (
      <section id="releases" className="kl-section kl-releases">
        {(section as any).title && (
          <header className="kl-sec-head">
            <h2 className="kl-sec-title">{(section as any).title}</h2>
          </header>
        )}
        {error ? (
          <div className="kl-note">{error}</div>
        ) : (
          <div className="kl-note">Sem lançamentos por enquanto.</div>
        )}
      </section>
    );
  }

  return (
    <section id="releases" className="kl-section kl-releases" aria-label="Últimos lançamentos">
      {(section as any).title && (
        <header className="kl-sec-head">
          <h2 className="kl-sec-title">{(section as any).title}</h2>
        </header>
      )}

      {error && <div className="kl-note" role="status">{error}</div>}

      <div className="kl-rel3d-stage">
        <div className="kl-rel3d-area" ref={areaRef}>
          {items.map((it, i) => (
            <React.Fragment key={i}>
              {/* CARD */}
              <div
                ref={i === active ? cardRef : undefined}
                className="kl-rel3d-card"
                style={styleFor(i)}
                onClick={() => (i === active ? null : setActive(i))}
              >
                <img className="kl-rel3d-cover" src={it.cover} alt={it.title} />
              </div>

              {/* REFLECTION (igual sandbox) */}
              <div className="kl-rel3d-reflection" style={styleFor(i)} />
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Barra inferior: setas + CTA */}
      {items[active] && (
        <div
          className="kl-rel3d-bottom"
          style={{
            width: bottomW,
            // expõe as variáveis CSS para o tamanho das setas e o espaçamento
            ['--rel3d-arrow-w' as any]: `${ARROW_W}px`,
            ['--rel3d-arrow-gap' as any]: `${ARROW_GAP}px`,
          }}
        >
          <button
            className="kl-rel3d-arrowTall"
            onClick={prev}
            aria-label="Anterior"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <div className="kl-rel-cta" style={{ width: cardW }}>
            <div className="kl-rel-cta-label">OUÇA EM</div>
            <div className="kl-rel-cta-links">
              {platformEntries(items[active].links).map(([k, href]) => (
                <a key={k} className="kl-rel-cta-chip" href={href} target="_blank" rel="noreferrer" aria-label={k}>
                  {iconFor(k)}
                </a>
              ))}
            </div>
          </div>

          <button
            className="kl-rel3d-arrowTall"
            onClick={next}
            aria-label="Próximo"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}